import type IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';

import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import Component from '@glimmer/component';

/**
 * Available BvStat types
 */
const TYPES = ['number', 'percent', 'currency', 'date', 'datetime'];

/**
 * Available BvStat sizes
 */
type TSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
const SIZES: TSize[] = ['xl', 'md', 'sm', 'lg', 'xs'];

type TValue = string | number | Date | moment.Moment;

interface IFormatterOptions {
  formatOptions?: object;
  emptyText?: string;
  showPlusSign?: boolean;
  type?: 'number' | 'percent' | 'currency' | 'date' | 'datetime';
  currency?: 'EUR' | 'USD' | 'GBP' | 'JPY';
}

interface IArgs extends IFormatterOptions {
  value?: TValue;
  previousValue?: number;
  displayPrevious?: boolean;
  title?: string;
  descType?: 'percent' | 'number' | 'diff';
  valueClassNames?: string;
  descClassNames?: string;
  showDesc?: boolean;
  showMovementArrow?: boolean;
  description?: string;
  size?: TSize;
  unit?: string;
  inline?: boolean;
  inverseDirection?: boolean;
}

/**
 * Component used to display stats, it can handle a title,
 * multiple value formats, a help text and variation vs previous value
 * It should be used in reports or dashboard.
 *
 */
export default class BvStat extends Component<IArgs> {
  @service declare intl: IntlService;

  /**
   * The description type of stat can be used to display variation in percent of absolute value
   * from the previousValue
   * Can be either 'percent' or 'number' (default to 'percent')
   */
  get descType() {
    return this.args.descType ?? 'percent';
  }

  /**
   * Currency in which the value is expressed. Default to Euro.
   * Only applicable when type = 'currency'
   */
  get currency() {
    return this.args.currency ?? 'EUR';
  }

  /**
   * The description is only shown when either the arrow, description or descType is filled
   */
  get hasDescription() {
    return this.args.showMovementArrow || this.args.description || this.args.descType;
  }

  /**
   * Type modifier className
   */
  get typeClass() {
    const { type } = this.args;
    return type && TYPES.includes(type) ? `bv-stat--${type}` : null;
  }

  /**
   * Size modifier className
   */
  get sizeClass() {
    const { size } = this.args;
    return size && SIZES.includes(size) ? `bv-stat--${size}` : null;
  }

  /**
   * Formatted Value
   */
  get displayedPreviousValue() {
    const { previousValue, formatOptions, emptyText, showPlusSign, type } = this.args;
    const currency = this.currency;
    return this._formatter(previousValue, {
      formatOptions,
      emptyText,
      showPlusSign,
      type,
      currency
    });
  }

  /**
   * Formatted previous value
   */
  get displayedValue() {
    const { value, formatOptions, emptyText, showPlusSign, type } = this.args;
    const currency = this.currency;
    return this._formatter(value, {
      formatOptions,
      emptyText,
      showPlusSign,
      type,
      currency
    });
  }

  /**
   * we have a diff if the numbers are different, or if the absolute values are different (useful for i.e. dates)
   */
  get hasDiff() {
    const precision = 5; // decimal precision for the diff. Totally arbitrary.
    return (
      Number(this.diff.toFixed(precision)) !== 0 && this.args.value !== this.args.previousValue
    );
  }

  /**
   * Calculated diff between value and previous value.
   * 0 if previousValue is not defined
   */
  get diff() {
    const { value, previousValue } = this.args;
    if (typeof value === 'number' && typeof previousValue === 'number') {
      return value - previousValue;
    }
    return 0;
  }

  /**
   * Diff value text color depending on the direction of the variation
   */
  get diffStyle() {
    let style = '';
    if (this.diff > 0) {
      style = this.args.inverseDirection ? 'u-color-danger' : 'u-color-success';
    } else if (this.diff < 0) {
      style = this.args.inverseDirection ? 'u-color-success' : 'u-color-danger';
    }
    return style;
  }

  /**
   * Calculated and formatted description
   */
  get displayedDesc() {
    const { description, value, previousValue, type } = this.args;
    if (description) {
      return description;
    }

    if (isEmpty(value) || isEmpty(previousValue)) {
      return '';
    }

    if (type === 'number' && typeof value === 'number' && typeof previousValue === 'number') {
      const format = this.args.formatOptions;
      const percent = value === 0 ? 0 : Math.abs(value / previousValue) - 1;
      if (this.descType === 'diff') {
        return this.intl.formatNumber(this.diff, format ?? { style: 'decimal' });
      } else if (this.descType === 'percent') {
        return this.intl.formatNumber(percent, format ?? { style: 'percent' });
      }
    }
    return '';
  }

  /**
   * Diff variation icon
   */
  get arrowDirection() {
    let icon = '';
    if (this.diff > 0) {
      icon = 'caret-up';
    } else if (this.diff < 0) {
      icon = 'caret-down';
    } else {
      icon = 'remove';
    }
    return icon;
  }

  /**
   * helper to format
   */
  private _formatter(
    value: TValue | undefined,
    { formatOptions, emptyText, showPlusSign, type, currency }: IFormatterOptions
  ) {
    if (isEmpty(value) && isPresent(emptyText)) {
      return emptyText;
    }

    if (isEmpty(value)) {
      return '';
    }

    let displayValue = value;

    if (type && ['number', 'percent', 'currency'].includes(type) && typeof value === 'number') {
      displayValue = this.intl.formatNumber(
        value,
        formatOptions || {
          style: type === 'number' ? undefined : type,
          maximumFractionDigits: 2,
          currency: type === 'currency' ? currency : undefined
        }
      );
      if (type === 'percent' && value > 0 && showPlusSign) {
        displayValue = '+' + displayValue;
      }
    } else if (type && ['date', 'datetime'].includes(type)) {
      displayValue = moment.utc(value).format('L');
    } else {
      displayValue = value;
    }
    return displayValue;
  }
}
